// Build an orthogonal SVG path (straight horizontal/vertical segments with
// rounded corners) from a list of waypoints. The corner radius is clamped
// per-corner so it never exceeds half of either adjacent segment's length
// — otherwise the quadratic would emit a zero-length curve and render as
// a sharp corner.
export function orthogonalPath(
  points: Array<[number, number]>,
  cornerRadius: number,
) {
  if (points.length < 2) return '';
  let d = `M ${points[0]![0]} ${points[0]![1]}`;
  for (let i = 1; i < points.length; i++) {
    const curr = points[i]!;
    const prev = points[i - 1]!;
    const next = points[i + 1];
    if (!next) {
      d += ` L ${curr[0]} ${curr[1]}`;
      continue;
    }
    const inDx = curr[0] - prev[0];
    const inDy = curr[1] - prev[1];
    const outDx = next[0] - curr[0];
    const outDy = next[1] - curr[1];
    const sameDir =
      Math.sign(inDx) === Math.sign(outDx) &&
      Math.sign(inDy) === Math.sign(outDy);
    if (sameDir) {
      d += ` L ${curr[0]} ${curr[1]}`;
      continue;
    }
    const inLen = Math.max(Math.abs(inDx), Math.abs(inDy));
    const outLen = Math.max(Math.abs(outDx), Math.abs(outDy));
    const r = Math.min(cornerRadius, inLen / 2, outLen / 2);
    const approach: [number, number] = [
      curr[0] - Math.sign(inDx) * r,
      curr[1] - Math.sign(inDy) * r,
    ];
    const depart: [number, number] = [
      curr[0] + Math.sign(outDx) * r,
      curr[1] + Math.sign(outDy) * r,
    ];
    d += ` L ${approach[0]} ${approach[1]} Q ${curr[0]} ${curr[1]} ${depart[0]} ${depart[1]}`;
  }
  return d;
}
